import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  X, 
  Check, 
  ChevronsUpDown, 
  FileText, 
  Receipt, 
  ShoppingCart, 
  RotateCcw,
  User,
  Building2,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Calculator,
  Plus,
  ArrowDown,
  Gift
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Screen } from "@/models/screen";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import { LogoUploadSection } from "./LogoUploadSection";
import { cn } from "@/lib/utils";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";
import { Factura, DocumentType, DocumentStatus } from "@/models/factura";

interface Vendor {
  full_name: string;
  email?: string;
  raw_user_meta_data?: {
    phone?: string;
    address?: string;
  };
}

interface FacturaFormProps {
  onClose: () => void;
  facturaToEdit?: Factura;
  initialDocumentType?: DocumentType;
  initialAnuncioData?: {
    logo_url?: string;
    adName?: string;
    screens?: Array<{
      id: string;
      name: string;
    }>;
    amount?: number;
    startDate?: string;
    endDate?: string;
  };
}

interface LineaFactura {
    pantalla: string;
    precio: number;
    cantidad_dias: number;
    descuento: number;
    impuesto: number;
    total: number;
    descripcion?: string;
    bonificada?: boolean;
}

interface Client {
  id: string;
  name: string;
  rnc?: string;
  phone?: string;
  email?: string;
  address?: string;
}

interface FormData {
  logo: File | null;
  ncf: string;
  client_id: string;
  cliente: string;
  rncCedula: string;
  telefono: string;
  correo: string;
  direccion: string;
  fecha: string;
  vencimiento: string;
  terminos_condiciones: string;
  notas: string;
  lineas: LineaFactura[];
  document_type: DocumentType;
  status: DocumentStatus;
  es_cortesia: boolean;
}

const initialFormData: FormData = {
  logo: null,
  ncf: "",
  client_id: "",
  cliente: "",
  rncCedula: "",
  telefono: "",
  correo: "",
  direccion: "",
  fecha: new Date().toISOString().split('T')[0],
  vencimiento: "",
  terminos_condiciones: "",
  notas: "",
  lineas: [{ pantalla: "", precio: 0, cantidad_dias: 1, descuento: 0, impuesto: 0, total: 0, bonificada: false }],
  document_type: 'cotizacion',
  status: 'draft',
  es_cortesia: false
};

export const FacturaForm: React.FC<FacturaFormProps> = ({ onClose, facturaToEdit, initialDocumentType = 'cotizacion', initialAnuncioData }) => {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();
  const { screens, refreshData, getAdById, updateAd } = useData();
  const { data: companyConfig } = useCompanyConfig();

  // Verificar permisos al cargar el formulario
  useEffect(() => {
    if (!user) {
      toast.error('Debes iniciar sesión para realizar esta acción');
      onClose();
      return;
    }
  }, [user, onClose]);

  // Consulta para obtener todas las facturas
  const { data: facturas = [], isLoading: isLoadingFacturas } = useQuery({
    queryKey: ['facturas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('facturas')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Factura[];
    }
  });

  // Consulta para obtener los clientes del vendedor
  const { data: clients = [], isLoading: isLoadingClients, error: clientsError } = useQuery({
    queryKey: ['clients', user?.id],
    queryFn: async () => {
      console.log('Fetching clients for user:', user?.id);
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('seller_id', user?.id)
        .order('name');

      if (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }
      console.log('Clients fetched:', data);
      return data;
    },
    enabled: !!user?.id
  });

  // Debug para mostrar información de clientes
  useEffect(() => {
    if (clientsError) {
      console.error('Error en consulta de clientes:', clientsError);
      toast.error('Error al cargar los clientes');
    }
  }, [clientsError]);

  const [formData, setFormData] = useState({
    logo: null as File | null,
    ncf: facturaToEdit?.ncf || "B0100000001",
    client_id: facturaToEdit?.client_id || "",
    cliente: facturaToEdit?.cliente || initialAnuncioData?.adName || "",
    fecha: facturaToEdit?.fecha || new Date().toISOString().split('T')[0],
    rncCedula: facturaToEdit?.rnc_cedula || "",
    vencimiento: facturaToEdit?.vencimiento || "",
    lineas: facturaToEdit?.lineas?.map(linea => ({
      pantalla: linea.pantalla,
      precio: linea.precio,
      cantidad_dias: linea.cantidad_dias,
      descuento: linea.descuento || 0,
      impuesto: linea.impuesto,
      descripcion: linea.descripcion,
      total: linea.total,
      bonificada: linea.bonificada || false
    })) || (initialAnuncioData ? initialAnuncioData.screens?.map(screen => ({
      pantalla: screen.id,
      precio: initialAnuncioData.amount || 0,
      cantidad_dias: calcularDiasEntreFechas(initialAnuncioData.startDate || '', initialAnuncioData.endDate || ''),
      descuento: 0,
      impuesto: 0,
      descripcion: `Anuncio publicitario del ${initialAnuncioData.startDate || ''} al ${initialAnuncioData.endDate || ''}`,
      total: initialAnuncioData.amount || 0,
      bonificada: false
    })) : [{
      pantalla: "",
      precio: 0,
      cantidad_dias: 1,
      descuento: 0,
      impuesto: 0,
      descripcion: "",
      total: 0,
      bonificada: false
    }]),
    terminos_condiciones: facturaToEdit?.terminos_condiciones || "",
    notas: facturaToEdit?.notas || "",
    telefono: facturaToEdit?.telefono || "",
    correo: facturaToEdit?.correo || "",
    direccion: facturaToEdit?.direccion || "",
    document_type: facturaToEdit?.document_type || initialDocumentType,
    status: facturaToEdit?.status || 'draft',
    es_cortesia: facturaToEdit?.es_cortesia || false
  });

  const [openScreenSelect, setOpenScreenSelect] = useState<{ [key: number]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState<{ [key: number]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");

  const filteredClients = useMemo(() => {
    if (!clientSearchQuery) return clients;
    const searchLower = clientSearchQuery.toLowerCase();
    return clients.filter(
      client => 
        client.name.toLowerCase().includes(searchLower) ||
        (client.rnc && client.rnc.toLowerCase().includes(searchLower))
    );
  }, [clients, clientSearchQuery]);

  const handleAddLine = () => {
    setFormData({
      ...formData,
      lineas: [...formData.lineas, {
        pantalla: "",
        precio: 0,
        cantidad_dias: 1,
        descuento: 0,
        impuesto: 0,
        descripcion: "",
        total: 0,
        bonificada: false
      }]
    });
  };

  const handleRemoveLine = (indexToRemove: number) => {
    if (formData.lineas.length > 1) {
      setFormData({
        ...formData,
        lineas: formData.lineas.filter((_, index) => index !== indexToRemove)
      });
    }
  };

  // Función para formatear números con separadores de miles
  const formatearNumero = (numero: number): string => {
    if (isNaN(numero)) return "0.00";
    return new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'decimal'
    }).format(numero);
  };

  // Función para limpiar el formato y obtener solo el número
  const limpiarFormato = (valor: string): number => {
    // Elimina todo excepto números y punto decimal
    const numeroLimpio = valor.replace(/[^\d.]/g, '');
    const numero = parseFloat(numeroLimpio);
    return isNaN(numero) ? 0 : numero;
  };

  // Función para manejar el cambio en el campo de precio
  const handlePrecioChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const valor = e.target.value;
    // Permitir solo números, punto decimal y backspace
    if (!/^\d*\.?\d*$/.test(valor) && valor !== '') return;

    const newLineas = [...formData.lineas];
    const precio = limpiarFormato(valor);
    const subtotalLinea = precio * newLineas[index].cantidad_dias;
    const descuentoLinea = subtotalLinea * (newLineas[index].descuento / 100);
    const totalLinea = newLineas[index].bonificada ? 0 : (subtotalLinea - descuentoLinea);
    
    newLineas[index] = {
      ...newLineas[index],
      precio: precio,
      total: totalLinea
    };
    setFormData({ ...formData, lineas: newLineas });
  };

  // Función para manejar el cambio en el campo de descuento
  const handleDescuentoChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const descuento = parseFloat(e.target.value) || 0;
    const newLineas = [...formData.lineas];
    const subtotalLinea = newLineas[index].precio * newLineas[index].cantidad_dias;
    const descuentoLinea = subtotalLinea * (descuento / 100);
    const totalLinea = newLineas[index].bonificada ? 0 : (subtotalLinea - descuentoLinea);
    
    newLineas[index] = {
      ...newLineas[index],
      descuento: descuento,
      total: totalLinea
    };
    setFormData({ ...formData, lineas: newLineas });
  };

  // Función para manejar el cambio en el campo de cantidad de días
  const handleCantidadDiasChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const dias = parseInt(e.target.value) || 1;
    const newLineas = [...formData.lineas];
    const subtotalLinea = newLineas[index].precio * dias;
    const descuentoLinea = subtotalLinea * (newLineas[index].descuento / 100);
    const totalLinea = newLineas[index].bonificada ? 0 : (subtotalLinea - descuentoLinea);
    
    newLineas[index] = {
      ...newLineas[index],
      cantidad_dias: dias,
      total: totalLinea
    };
    setFormData({ ...formData, lineas: newLineas });
  };

  // Función para manejar el checkbox de bonificación
  const handleBonificadaChange = (checked: boolean, index: number) => {
    const newLineas = [...formData.lineas];
    const subtotalLinea = newLineas[index].precio * newLineas[index].cantidad_dias;
    const descuentoLinea = subtotalLinea * (newLineas[index].descuento / 100);
    const totalLinea = checked ? 0 : (subtotalLinea - descuentoLinea);
    
    newLineas[index] = {
      ...newLineas[index],
      bonificada: checked,
      total: totalLinea
    };
    setFormData({ ...formData, lineas: newLineas });
  };

  const calcularTotales = () => {
    // Si es cortesía, todos los valores son cero
    if (formData.es_cortesia) {
      return {
        subtotal: 0,
        descuentos: 0,
        impuestos: 0,
        total: 0
      };
    }

    // Filtrar solo líneas NO bonificadas para los cálculos
    const lineasNoBonificadas = formData.lineas.filter(linea => !linea.bonificada);

    const subtotalBruto = lineasNoBonificadas.reduce((acc, linea) => 
      acc + (linea.precio * linea.cantidad_dias), 0
    );
    
    const descuentos = lineasNoBonificadas.reduce((acc, linea) => 
      acc + ((linea.precio * linea.cantidad_dias) * (linea.descuento / 100)), 0
    );
    
    const subtotal = subtotalBruto - descuentos;
    
    const impuestos = lineasNoBonificadas.reduce((acc, linea) => 
      acc + (linea.total * linea.impuesto / 100), 0
    );
    
    return {
      subtotal,
      descuentos,
      impuestos,
      total: subtotal + impuestos
    };
  };

  const { subtotal, descuentos, impuestos, total } = calcularTotales();

  // Función para calcular días entre fechas
  const calcularDiasEntreFechas = (fechaInicio: string, fechaFin: string) => {
    if (!fechaInicio || !fechaFin) return 0;
    
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diferencia = fin.getTime() - inicio.getTime();
    const dias = Math.ceil(diferencia / (1000 * 3600 * 24)) + 1; // +1 para incluir el día inicial
    return dias > 0 ? dias : 0;
  };

  // Efecto para actualizar los días cuando cambien las fechas
  useEffect(() => {
    if (formData.fecha && formData.vencimiento) {
      const dias = calcularDiasEntreFechas(formData.fecha, formData.vencimiento);
      // Actualizar todas las líneas con la nueva cantidad de días
      const nuevasLineas = formData.lineas.map(linea => ({
        ...linea,
        cantidad_dias: dias,
        total: linea.precio * dias
      }));
      setFormData(prev => ({
        ...prev,
        lineas: nuevasLineas
      }));
    }
  }, [formData.fecha, formData.vencimiento]);

  // Efecto para actualizar los datos del cliente cuando se selecciona uno
  useEffect(() => {
    if (formData.client_id) {
      const selectedClient = clients.find(c => c.id === formData.client_id);
      if (selectedClient) {
        setFormData(prev => ({
          ...prev,
          cliente: selectedClient.name,
          rncCedula: selectedClient.rnc,
          telefono: selectedClient.phone || "",
          correo: selectedClient.email || "",
          direccion: selectedClient.address || ""
        }));
      }
    }
  }, [formData.client_id, clients]);

  // Función para filtrar pantallas
  const getFilteredScreens = (index: number) => {
    const query = searchQuery[index]?.toLowerCase() || '';
    if (!query) return screens;
    
    return screens.filter(screen => 
      screen.name.toLowerCase().includes(query) || 
      (screen.location && screen.location.toLowerCase().includes(query))
    );
  };

  // Función para actualizar el anuncio asociado
  const updateAssociatedAd = async (facturaId: string, adId: string) => {
    try {
      console.log('=== INICIANDO ACTUALIZACIÓN DEL ANUNCIO ===');
      console.log('ID de la factura:', facturaId);
      console.log('ID del anuncio:', adId);
      console.log('Datos del formulario:', formData);

      // Obtener los IDs de las pantallas seleccionadas desde formData.lineas
      // formData.lineas contiene objetos con pantalla que es el ID de la pantalla
      const selectedScreenIds = formData.lineas
        .filter(linea => linea.pantalla)
        .map(linea => linea.pantalla); // pantalla ya contiene el screen ID

      console.log('IDs de pantallas seleccionadas:', selectedScreenIds);

      if (selectedScreenIds.length === 0) {
        console.error('No hay pantallas seleccionadas');
        toast.error('No hay pantallas seleccionadas para actualizar');
        return false;
      }

      // Obtener información completa de las pantallas usando los IDs
      const { data: selectedScreens, error: screensError } = await supabase
        .from('screens')
        .select('id, name, location')
        .in('id', selectedScreenIds);

      if (screensError) {
        console.error('Error al obtener pantallas:', screensError);
        toast.error('Error al obtener información de las pantallas');
        return false;
      }

      console.log('Pantallas obtenidas:', selectedScreens);

      if (!selectedScreens || selectedScreens.length === 0) {
        console.error('No se encontraron pantallas con los IDs proporcionados');
        toast.error('No se encontraron las pantallas seleccionadas');
        return false;
      }

      // Preparar el JSON de pantallas para el anuncio
      const screensJson = selectedScreens.map(screen => ({
        screenId: screen.id,
        screenName: screen.name
      }));

      console.log('JSON de pantallas preparado:', screensJson);

      // Calcular duración en días
      const duration = calcularDiasEntreFechas(formData.fecha, formData.vencimiento);
      console.log('Duración calculada:', duration, 'días');

      // Preparar los datos para actualizar el anuncio
      const adUpdateData = {
        client_name: formData.cliente,
        start_date: formData.fecha,
        end_date: formData.vencimiento,
        amount: Number(total),
        screen_id: selectedScreenIds[0], // Pantalla principal
        screens_json: JSON.stringify(screensJson),
        duration: duration,
        updated_at: new Date().toISOString()
      };

      console.log('Datos para actualizar el anuncio:', adUpdateData);

      // Ejecutar la actualización del anuncio
      const { data: updatedAd, error: adUpdateError } = await supabase
        .from('ads')
        .update(adUpdateData)
        .eq('id', adId)
        .select()
        .single();

      if (adUpdateError) {
        console.error('Error detallado al actualizar anuncio:', {
          message: adUpdateError.message,
          details: adUpdateError.details,
          hint: adUpdateError.hint,
          code: adUpdateError.code
        });
        toast.error(`Error al actualizar anuncio: ${adUpdateError.message}`);
        return false;
      }

      console.log('Anuncio actualizado exitosamente:', updatedAd);
      return true;

    } catch (error) {
      console.error('Error inesperado al actualizar anuncio:', error);
      toast.error('Error inesperado al actualizar el anuncio');
      return false;
    }
  };

  const handleGuardarFactura = async () => {
    try {
      if (!user) {
        toast.error('Debes iniciar sesión para realizar esta acción');
        return;
      }

      // Validaciones básicas
      if (!formData.client_id) {
        toast.error('Debes seleccionar un cliente');
        return;
      }

      if (!formData.fecha) {
        toast.error('La fecha es requerida');
        return;
      }

      if (formData.lineas.length === 0) {
        toast.error('Debe agregar al menos una línea a la factura');
        return;
      }

      // Validar que todas las líneas tengan pantalla y precio (excepto las bonificadas)
      const lineasInvalidas = formData.lineas.some(linea => 
        !linea.pantalla || (!linea.bonificada && linea.precio <= 0)
      );

      if (lineasInvalidas) {
        toast.error('Todas las líneas deben tener una pantalla seleccionada y las líneas no bonificadas deben tener un precio mayor a 0');
        return;
      }

      let logoUrl = null;

      // Si hay un logo, subirlo a Supabase Storage
      if (formData.logo) {
        const fileExt = formData.logo.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `facturas-logos/${fileName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('facturas')
          .upload(filePath, formData.logo);

        if (uploadError) {
          console.error('Error al subir el logo:', uploadError);
          toast.error('Error al subir el logo');
          return;
        }

        // Obtener la URL pública del logo
        const { data: { publicUrl } } = supabase.storage
          .from('facturas')
          .getPublicUrl(filePath);

        logoUrl = publicUrl;
      }

      // Preparar los datos del anuncio
      const adData = {
        client_name: formData.cliente,
        start_date: formData.fecha,
        end_date: formData.vencimiento,
        amount: formData.es_cortesia ? 0 : total,
        screen_id: formData.lineas[0].pantalla,
        screens_json: formData.lineas.map(linea => ({
          screenId: linea.pantalla,
          screenName: screens.find(s => s.id === linea.pantalla)?.name || ''
        })),
        seller_id: user.id,
        seller_name: user.user_metadata?.full_name || '',
        seller_email: user.email || '',
        type: 'image',
        ad_type: formData.es_cortesia ? 'courtesy' : 'sale',
        is_hidden: false,
        is_expired: false,
        version: 1,
        material_url: ''
      };

      // Crear facturaData base sin los campos nuevos primero
      const facturaDataBase = {
        ncf: formData.ncf,
        client_id: formData.client_id,
        cliente: formData.cliente,
        fecha: formData.fecha,
        rnc_cedula: formData.rncCedula,
        vencimiento: formData.vencimiento,
        lineas: formData.lineas,
        terminos_condiciones: formData.es_cortesia 
          ? `${formData.terminos_condiciones}\n\n[CORTESÍA]`.trim() 
          : formData.terminos_condiciones,
        notas: formData.notas,
        subtotal,
        impuestos,
        total,
        user_id: user.id,
        logo_url: logoUrl,
        es_cortesia: formData.es_cortesia
      };

      // Intentar agregar los nuevos campos, pero si fallan, continuar sin ellos
      const facturaData = {
        ...facturaDataBase,
        telefono: formData.telefono,
        correo: formData.correo,
        direccion: formData.direccion,
        document_type: formData.document_type,
        status: formData.status,
        es_cortesia: formData.es_cortesia
      };

      let facturaId;

      if (facturaToEdit) {
        // Si hay un logo anterior y estamos subiendo uno nuevo, eliminar el anterior
        if (facturaToEdit.logo_url && logoUrl) {
          const oldLogoPath = facturaToEdit.logo_url.split('/').pop();
          if (oldLogoPath) {
            await supabase.storage
              .from('facturas')
              .remove([`facturas-logos/${oldLogoPath}`]);
          }
        }

        // Intentar actualizar factura existente con todos los campos
        let { data: updatedFactura, error: facturaError } = await supabase
          .from('facturas')
          .update(facturaData)
          .eq('id', facturaToEdit.id)
          .select()
          .single();

        // Si hay error por campos inexistentes, intentar sin los campos nuevos
        if (facturaError && (facturaError.message.includes('telefono') || facturaError.message.includes('correo') || facturaError.message.includes('direccion'))) {
          console.log('Intentando actualizar factura sin campos de contacto...');
          const result = await supabase
            .from('facturas')
            .update({
              ...facturaDataBase,
              document_type: formData.document_type,
              status: formData.status,
              es_cortesia: formData.es_cortesia
            })
            .eq('id', facturaToEdit.id)
            .select()
            .single();
          
          updatedFactura = result.data;
          facturaError = result.error;
        }

        if (facturaError) throw facturaError;
        facturaId = facturaToEdit.id;

        // Si hay un anuncio asociado, actualizarlo
        if (facturaToEdit.ad_id) {
          const { error: adError } = await supabase
            .from('ads')
            .update(adData)
            .eq('id', facturaToEdit.ad_id);

          if (adError) throw adError;
        }

        // Verificar si se guardaron los campos de contacto
        if (updatedFactura && (updatedFactura.telefono || updatedFactura.correo || updatedFactura.direccion)) {
          toast.success('Factura actualizada exitosamente con información de contacto');
        } else {
          toast.success('Factura actualizada exitosamente (sin campos de contacto - requiere migración de BD)');
        }
      } else {
        let newAd = null;
        
        // Solo crear anuncio si es una factura aprobada
        if (formData.document_type === 'factura' && formData.status === 'approved') {
          // Crear nuevo anuncio primero
          const { data: adData_result, error: adError } = await supabase
            .from('ads')
            .insert([{
              ...adData,
              type: 'image',
              ad_type: formData.es_cortesia ? 'courtesy' : 'sale',
              material_url: '',
              is_hidden: false,
              is_expired: false,
              version: 1
            }])
            .select()
            .single();

          if (adError) throw adError;
          newAd = adData_result;
        }

        // Intentar crear nueva factura con todos los campos
        let { data: newFactura, error: facturaError } = await supabase
          .from('facturas')
          .insert([{
            ...facturaData,
            ad_id: newAd?.id || null
          }])
          .select()
          .single();

        // Si hay error por campos inexistentes, intentar sin los campos nuevos
        if (facturaError && (facturaError.message.includes('telefono') || facturaError.message.includes('correo') || facturaError.message.includes('direccion'))) {
          console.log('Intentando crear factura sin campos de contacto...');
          const result = await supabase
            .from('facturas')
            .insert([{
              ...facturaDataBase,
              document_type: formData.document_type,
              status: formData.status,
              es_cortesia: formData.es_cortesia,
              ad_id: newAd?.id || null
            }])
            .select()
            .single();
          
          newFactura = result.data;
          facturaError = result.error;
        }

        if (facturaError) throw facturaError;
        facturaId = newFactura.id;

        // Verificar si se guardaron los campos de contacto y mostrar mensaje apropiado
        if (formData.document_type === 'cotizacion') {
          toast.success('Cotización creada como borrador. Use "Convertir a Factura" para aprobarla y generar el anuncio.');
        } else if (formData.document_type === 'orden_compra') {
          toast.success('Orden de compra creada como borrador. Use "Convertir a Factura" para aprobarla y generar el anuncio.');
        } else if (formData.document_type === 'nota_credito') {
          toast.success('Nota de crédito creada como borrador. Use "Convertir a Factura" para aprobarla y generar el anuncio.');
        } else if (newFactura && (newFactura.telefono || newFactura.correo || newFactura.direccion)) {
          toast.success('Factura creada exitosamente con información de contacto y anuncio registrado');
        } else {
          toast.success('Factura creada exitosamente y anuncio registrado');
        }
      }

      // Refrescar los datos
      await refreshData();
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      queryClient.invalidateQueries({ queryKey: ['ads'] });

      // Cerrar el formulario
      onClose();

    } catch (error: any) {
      console.error('Error al guardar la factura:', error);
      console.error('Error details:', error.message || error);
      
      // Verificar si el error es por columnas que no existen
      if (error.message && (error.message.includes('telefono') || error.message.includes('correo') || error.message.includes('direccion'))) {
        toast.error('Error: Las columnas de contacto del cliente no existen en la base de datos. Contacta al administrador.');
      } else {
        toast.error(`Error al guardar la factura: ${error.message || 'Error desconocido'}`);
      }
    }
  };

  // Funciones para manejo de tipos de documento
  const getDocumentTypeLabel = (type: DocumentType): string => {
    const labels = {
      'factura': 'Factura',
      'cotizacion': 'Cotización', 
      'orden_compra': 'Orden de Compra',
      'nota_credito': 'Nota de Crédito'
    };
    return labels[type];
  };

  const getDocumentTypeIcon = (type: DocumentType) => {
    const icons = {
      'factura': Receipt,
      'cotizacion': FileText,
      'orden_compra': ShoppingCart,
      'nota_credito': RotateCcw
    };
    return icons[type];
  };

  const handleApproveDocument = async () => {
    if (formData.document_type === 'factura') return;
    
    try {
      setIsLoading(true);
      
      if (!facturaToEdit?.id) {
        toast.error('No se puede aprobar un documento que no ha sido guardado');
        return;
      }

      // Preparar los datos del anuncio
      const adData = {
        client_name: formData.cliente,
        start_date: formData.fecha,
        end_date: formData.vencimiento,
        amount: formData.es_cortesia ? 0 : total,
        screen_id: formData.lineas[0].pantalla,
        screens_json: formData.lineas.map(linea => ({
          screenId: linea.pantalla,
          screenName: screens.find(s => s.id === linea.pantalla)?.name || ''
        })),
        seller_id: user.id,
        seller_name: user.user_metadata?.full_name || '',
        seller_email: user.email || '',
        type: 'image',
        ad_type: formData.es_cortesia ? 'courtesy' : 'sale',
        is_hidden: false,
        is_expired: false,
        version: 1,
        material_url: ''
      };

      // Crear el anuncio
      const { data: newAd, error: adError } = await supabase
        .from('ads')
        .insert([adData])
        .select()
        .single();

      if (adError) throw adError;

      // Actualizar el documento para convertirlo en factura aprobada
      const { error: updateError } = await supabase
        .from('facturas')
        .update({
          document_type: 'factura',
          status: 'approved',
          ad_id: newAd.id,
          es_cortesia: formData.es_cortesia
        })
        .eq('id', facturaToEdit.id);

      if (updateError) throw updateError;

      // Actualizar el estado local
      setFormData({
        ...formData,
        document_type: 'factura' as DocumentType,
        status: 'approved' as DocumentStatus
      });

      // Refrescar los datos
      await refreshData();
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      queryClient.invalidateQueries({ queryKey: ['cotizaciones'] });
      queryClient.invalidateQueries({ queryKey: ['ordenes-compra'] });
      queryClient.invalidateQueries({ queryKey: ['notas-credito'] });

      toast.success(`${getDocumentTypeLabel(formData.document_type)} convertida a factura y anuncio creado exitosamente`);
      
      // Cerrar el formulario después de un momento
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error al aprobar documento:', error);
      toast.error('Error al convertir a factura');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
        <div className="space-y-6 max-w-[1200px] mx-auto">
          {/* Sección: Tipo de Documento */}
          <Card className="border-l-4 border-l-primary overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Información del Documento
                </CardTitle>
                {formData.document_type !== 'factura' && formData.status === 'draft' && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleApproveDocument}
                    className="text-xs"
                    disabled={isLoading}
                  >
                    Convertir a Factura
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Tipo de documento</Label>
                <RadioGroup
                  value={formData.document_type}
                  onValueChange={(value) => {
                    if (facturaToEdit && formData.status === 'approved' && formData.document_type === 'factura') {
                      toast.error('No se puede cambiar el tipo de una factura aprobada');
                      return;
                    }
                    setFormData({ 
                      ...formData, 
                      document_type: value as DocumentType,
                      status: value === 'factura' ? 'approved' : 'draft'
                    });
                  }}
                  className="flex flex-wrap gap-4"
                >
                  {(['factura', 'cotizacion', 'orden_compra', 'nota_credito'] as DocumentType[]).map((type) => {
                    const Icon = getDocumentTypeIcon(type);
                    const isSelected = formData.document_type === type;
                    const isDisabled = facturaToEdit && formData.status === 'approved' && formData.document_type === 'factura';
                    
                    return (
                      <div key={type} className="flex items-center space-x-2">
                        <RadioGroupItem 
                          value={type} 
                          id={type}
                          disabled={isDisabled}
                          className="sr-only"
                        />
                        <Label
                          htmlFor={type}
                          className={cn(
                            "flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all hover:shadow-md",
                            isSelected 
                              ? type === 'factura' 
                                ? "bg-green-600 text-white border-green-600 shadow-md" 
                                : "bg-primary text-primary-foreground border-primary shadow-md"
                              : "bg-background hover:bg-accent border-border",
                            isDisabled && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm font-medium">{getDocumentTypeLabel(type)}</span>
                          {formData.status === 'approved' && type === formData.document_type && type === 'factura' && (
                            <Badge variant="secondary" className="text-xs">Aprobado</Badge>
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              </div>

              {/* Mensaje informativo para cotizaciones/órdenes/notas */}
              {formData.document_type !== 'factura' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900">
                        {formData.document_type === 'cotizacion' ? 'Cotización en borrador' :
                         formData.document_type === 'orden_compra' ? 'Orden de compra en borrador' :
                         'Nota de crédito en borrador'}
                      </h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Este documento se guardará como borrador. Para registrar ventas y anuncios, 
                        debe usar el botón "Convertir a Factura" después de guardarlo.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Opción de Cortesía */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 rounded-lg border border-orange-200 bg-orange-50">
                  <Checkbox
                    id="es_cortesia"
                    checked={formData.es_cortesia}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, es_cortesia: !!checked })
                    }
                  />
                  <div className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-orange-600" />
                    <Label htmlFor="es_cortesia" className="text-sm font-medium text-orange-900 cursor-pointer">
                      Marcar como Cortesía
                    </Label>
                  </div>
                </div>
                {formData.es_cortesia && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs text-orange-700">
                      <strong>Cortesía activada:</strong> Los montos de esta factura serán establecidos en cero. 
                      Puedes seleccionar pantallas y configurar servicios normalmente.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <LogoUploadSection
                    logo={formData.logo}
                    onLogoChange={(file) => setFormData({ ...formData, logo: file })}
                  />
                  {!formData.logo && companyConfig?.company_logo_url && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <img
                          src={companyConfig.company_logo_url}
                          alt="Logo de empresa"
                          className="w-8 h-8 object-contain"
                        />
                        <div>
                          <p className="text-xs font-medium text-blue-900">Logo de empresa configurado</p>
                          <p className="text-xs text-blue-700">Se usará automáticamente si no subes uno específico</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    NCF
                  </Label>
                  <Input
                    value={formData.ncf}
                    onChange={(e) => setFormData({ ...formData, ncf: e.target.value })}
                    className="h-10"
                    placeholder="Número de comprobante fiscal"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sección: Información del Cliente */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <User className="h-5 w-5" />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Cliente
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between h-10 px-3 text-left font-normal"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenClientDialog(true);
                    }}
                  >
                    {formData.client_id
                      ? clients.find((client) => client.id === formData.client_id)?.name || "Seleccionar cliente"
                      : "Seleccionar cliente"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>

                  <Dialog 
                    open={openClientDialog} 
                    onOpenChange={(open) => {
                      setOpenClientDialog(open);
                      if (!open) {
                        setClientSearchQuery("");
                      }
                    }}
                  >
                    <DialogContent className="max-w-[400px] p-0" onClick={(e) => e.stopPropagation()}>
                      <DialogHeader className="px-4 py-2 border-b">
                        <DialogTitle>Seleccionar Cliente</DialogTitle>
                      </DialogHeader>
                      
                      <div className="p-4 space-y-4">
                        <div className="sticky top-0 bg-white">
                          <Input
                            type="text"
                            placeholder="Buscar cliente..."
                            className="h-8"
                            value={clientSearchQuery}
                            onChange={(e) => setClientSearchQuery(e.target.value)}
                          />
                        </div>
                        
                        <div className="max-h-[300px] overflow-auto -mx-4">
                          {isLoadingClients ? (
                            <div className="px-4 py-2 text-sm text-gray-500">
                              Cargando clientes...
                            </div>
                          ) : filteredClients.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500">
                              {clientSearchQuery ? 'No se encontraron clientes con ese criterio.' : 'No tienes clientes registrados. Ve a la sección de clientes para crear uno.'}
                            </div>
                          ) : (
                            filteredClients.map((client) => (
                              <button
                                key={client.id}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                                  formData.client_id === client.id ? 'bg-gray-100' : ''
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFormData({
                                    ...formData,
                                    client_id: client.id,
                                    cliente: client.name,
                                    rncCedula: client.rnc || "",
                                    telefono: client.phone || "",
                                    correo: client.email || "",
                                    direccion: client.address || ""
                                  });
                                  setOpenClientDialog(false);
                                  setClientSearchQuery("");
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.client_id === client.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div>
                                  <div className="font-medium">{client.name}</div>
                                  {client.rnc && (
                                    <div className="text-gray-500 text-xs">RNC: {client.rnc}</div>
                                  )}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Fecha inicial
                  </Label>
                  <Input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    className="h-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">RNC o Cédula</Label>
                  <Input
                    value={formData.rncCedula}
                    onChange={(e) => setFormData({ ...formData, rncCedula: e.target.value })}
                    className="h-10"
                    placeholder="RNC o cédula del cliente"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Fecha final
                  </Label>
                  <Input
                    type="date"
                    value={formData.vencimiento}
                    min={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, vencimiento: e.target.value })}
                    className="h-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Teléfono
                  </Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Correo electrónico
                  </Label>
                  <Input
                    type="email"
                    value={formData.correo}
                    onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                    className="h-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Dirección
                  </Label>
                  <Input
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                    className="h-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sección: Líneas de Factura */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Líneas de Factura
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <div className="min-w-[800px] w-full">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-2 w-[250px] text-sm">Pantalla</th>
                      <th className="text-right py-2 px-2 w-[120px] text-sm">Precio</th>
                      <th className="text-right py-2 px-2 w-[150px] text-sm">Cantidad de días</th>
                      <th className="text-right py-2 px-2 w-[120px] text-sm">Descuento %</th>
                      <th className="text-right py-2 px-2 w-[120px] text-sm">Impuesto %</th>
                      <th className="text-center py-2 px-2 w-[80px] text-sm">Bonificar</th>
                      <th className="text-right py-2 px-2 w-[120px] text-sm">Total</th>
                      <th className="w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lineas.map((linea, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2 pr-4">
                          <div className="w-full">
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-between h-8 px-3 text-left font-normal"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenScreenSelect(prev => ({ ...prev, [index]: true }));
                              }}
                            >
                              {linea.pantalla
                                ? screens.find((screen) => screen.id === linea.pantalla)?.name || "Seleccione una pantalla"
                                : "Seleccione una pantalla"}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>

                            <Dialog 
                              open={openScreenSelect[index]} 
                              onOpenChange={(open) => {
                                setOpenScreenSelect(prev => ({ ...prev, [index]: open }));
                                if (!open) {
                                  setSearchQuery(prev => ({ ...prev, [index]: '' }));
                                }
                              }}
                            >
                              <DialogContent className="max-w-[400px] p-0" onClick={(e) => e.stopPropagation()}>
                                <DialogHeader className="px-4 py-2 border-b">
                                  <DialogTitle>Seleccionar Pantalla</DialogTitle>
                                </DialogHeader>
                                
                                <div className="p-4 space-y-4">
                                  <div className="sticky top-0 bg-white">
                                    <Input
                                      type="text"
                                      placeholder="Buscar pantalla..."
                                      className="h-8"
                                      value={searchQuery[index] || ''}
                                      onChange={(e) => {
                                        setSearchQuery(prev => ({ ...prev, [index]: e.target.value }));
                                      }}
                                    />
                                  </div>
                                  
                                  <div className="max-h-[300px] overflow-auto -mx-4">
                                    {getFilteredScreens(index).length === 0 ? (
                                      <div className="px-4 py-2 text-sm text-gray-500">
                                        No se encontraron pantallas.
                                      </div>
                                    ) : (
                                      getFilteredScreens(index).map((screen) => (
                                        <button
                                          key={screen.id}
                                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${
                                            linea.pantalla === screen.id ? 'bg-gray-100' : ''
                                          }`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const newLineas = [...formData.lineas];
                                            newLineas[index].pantalla = screen.id;
                                            setFormData({ ...formData, lineas: newLineas });
                                            setOpenScreenSelect(prev => ({ ...prev, [index]: false }));
                                            setSearchQuery(prev => ({ ...prev, [index]: '' }));
                                          }}
                                        >
                                          <Check
                                            className={`mr-2 h-4 w-4 ${
                                              linea.pantalla === screen.id ? "opacity-100" : "opacity-0"
                                            }`}
                                          />
                                          <div>
                                            <div className="font-medium">{screen.name}</div>
                                            {screen.location && (
                                              <div className="text-gray-500 text-xs">{screen.location}</div>
                                            )}
                                          </div>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <div className="relative flex items-center">
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={linea.precio === 0 ? '' : linea.precio.toString()}
                              onChange={(e) => handlePrecioChange(e, index)}
                              onBlur={(e) => {
                                const newLineas = [...formData.lineas];
                                const precio = limpiarFormato(e.target.value);
                                const subtotalLinea = precio * newLineas[index].cantidad_dias;
                                const descuentoLinea = subtotalLinea * (newLineas[index].descuento / 100);
                                const totalLinea = newLineas[index].bonificada ? 0 : (subtotalLinea - descuentoLinea);
                                
                                newLineas[index] = {
                                  ...newLineas[index],
                                  precio: precio,
                                  total: totalLinea
                                };
                                setFormData({ ...formData, lineas: newLineas });
                              }}
                              onFocus={(e) => {
                                if (linea.precio === 0) {
                                  e.target.value = '';
                                }
                              }}
                              placeholder={linea.bonificada ? "N/A (Bonificada)" : "0.00"}
                              disabled={linea.bonificada}
                              className={`h-8 text-right px-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${linea.bonificada ? 'bg-green-50 text-green-600' : ''}`}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              value={linea.cantidad_dias}
                              onChange={(e) => handleCantidadDiasChange(e, index)}
                              className="h-8 text-right"
                              min="1"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="h-8 px-2 text-xs"
                              onClick={() => {
                                const newLineas = [...formData.lineas];
                                for (let i = index + 1; i < newLineas.length; i++) {
                                  const subtotalLinea = newLineas[i].precio * newLineas[index].cantidad_dias;
                                  const descuentoLinea = subtotalLinea * (newLineas[i].descuento / 100);
                                  const totalLinea = subtotalLinea - descuentoLinea;
                                  
                                  newLineas[i].cantidad_dias = newLineas[index].cantidad_dias;
                                  newLineas[i].total = totalLinea;
                                }
                                setFormData({ ...formData, lineas: newLineas });
                              }}
                              title="Aplicar esta cantidad de días a las líneas siguientes"
                            >
                              ↓
                            </Button>
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              value={linea.descuento}
                              onChange={(e) => handleDescuentoChange(e, index)}
                              disabled={linea.bonificada}
                              placeholder={linea.bonificada ? "N/A" : "0"}
                              className={`h-8 text-right ${linea.bonificada ? 'bg-green-50 text-green-600' : ''}`}
                              min="0"
                              max="100"
                              step="0.01"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="h-8 px-2 text-xs"
                              onClick={() => {
                                const newLineas = [...formData.lineas];
                                for (let i = index + 1; i < newLineas.length; i++) {
                                  const subtotalLinea = newLineas[i].precio * newLineas[i].cantidad_dias;
                                  const descuentoLinea = subtotalLinea * (newLineas[index].descuento / 100);
                                  const totalLinea = subtotalLinea - descuentoLinea;
                                  
                                  newLineas[i].descuento = newLineas[index].descuento;
                                  newLineas[i].total = totalLinea;
                                }
                                setFormData({ ...formData, lineas: newLineas });
                              }}
                              title="Aplicar este descuento a las líneas siguientes"
                            >
                              ↓
                            </Button>
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              value={linea.impuesto}
                              onChange={(e) => {
                                const newLineas = [...formData.lineas];
                                newLineas[index].impuesto = Number(e.target.value);
                                setFormData({ ...formData, lineas: newLineas });
                              }}
                              disabled={linea.bonificada}
                              placeholder={linea.bonificada ? "N/A" : "0"}
                              className={`h-8 text-right ${linea.bonificada ? 'bg-green-50 text-green-600' : ''}`}
                              min="0"
                              max="100"
                              step="0.01"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="h-8 px-2 text-xs"
                              onClick={() => {
                                const newLineas = [...formData.lineas];
                                for (let i = index + 1; i < newLineas.length; i++) {
                                  newLineas[i].impuesto = newLineas[index].impuesto;
                                }
                                setFormData({ ...formData, lineas: newLineas });
                              }}
                              title="Aplicar este impuesto a las líneas siguientes"
                            >
                              ↓
                            </Button>
                          </div>
                        </td>
                        <td className="px-2 py-1 text-center">
                          <div className="flex justify-center items-center">
                            <Checkbox
                              checked={linea.bonificada || false}
                              onCheckedChange={(checked) => handleBonificadaChange(!!checked, index)}
                              className="h-4 w-4"
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1 text-right whitespace-nowrap text-sm">
                          {linea.bonificada ? (
                            <div className="flex items-center gap-1 justify-end">
                              <Gift className="h-4 w-4 text-green-600" />
                              <span className="text-green-600 font-medium">GRATIS</span>
                            </div>
                          ) : (
                            <span>RD$ {formatearNumero(linea.total)}</span>
                          )}
                        </td>
                        <td className="pl-1 py-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-100"
                            onClick={() => handleRemoveLine(index)}
                            disabled={formData.lineas.length === 1}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button 
                type="button" 
                variant="outline" 
                onClick={handleAddLine}
                className="h-8 text-sm"
              >
                + Agregar línea
              </Button>

              <div className="text-right space-y-0.5 mt-2 border-t pt-2">
                <div className="text-sm text-gray-600">
                  Subtotal: RD$ {formatearNumero(subtotal)}
                </div>
                <div className="text-sm text-gray-600">
                  Descuentos: RD$ {formatearNumero(descuentos)}
                </div>
                <div className="text-sm text-gray-600">
                  Impuestos: RD$ {formatearNumero(impuestos)}
                </div>
                <div className="text-base font-semibold">
                  Total: RD$ {formatearNumero(total)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sección: Términos y Condiciones */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Términos y Condiciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Términos y condiciones</Label>
                <Textarea
                  placeholder="Visible en la impresión del documento"
                  value={formData.terminos_condiciones}
                  onChange={(e) => setFormData({ ...formData, terminos_condiciones: e.target.value })}
                  className="h-20 resize-none text-sm"
                />
              </div>
              <div className="space-y-3">
                <Label className="text-sm font-medium">Notas</Label>
                <Textarea
                  placeholder="Notas adicionales para esta venta"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className="h-20 resize-none text-sm"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-end gap-2 p-6 border-t bg-gray-50 shrink-0">
        <Button variant="outline" onClick={onClose} className="h-8 text-sm">
          Cancelar
        </Button>
        <Button 
          className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-sm"
          onClick={handleGuardarFactura}
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : (facturaToEdit ? 'Actualizar factura' : 'Guardar y generar factura')}
        </Button>
      </div>
    </div>
  );
};

export default FacturaForm;
